import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Briefcase,
  Building,
  Shield,
  Settings as SettingsIcon,
  ChevronRight,
  Package,
  UserCog,
  Headset,
} from "lucide-react";
import { isHelpRequestEnabled } from "../utils/featureFlags";

function Profile() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const productDetails = useAuthStore((state) => state.productDetails);

  const profileInfo = [
    { icon: Mail, label: "Email", value: user?.email },
    { icon: Phone, label: "Phone", value: user?.phone || "Not provided" },
    { icon: Briefcase, label: "Designation", value: user?.designation },
    { icon: Building, label: "Department", value: user?.department },
    { icon: Shield, label: "User Level", value: user?.user_level },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-300 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-white/20 dark:border-gray-800 shadow-lg safe-top transition-colors duration-300">
        <div className="px-6 py-5 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Profile
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6 pb-32">
        {/* Profile Card */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 mb-6">
          {/* Avatar and Name */}
          <div className="flex flex-col items-center mb-6">
            <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mb-4 shadow-xl">
              <User className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{user?.name}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {user?.employee_type || "Employee"}
            </p>
          </div>

          {/* Profile Information */}
          <div className="space-y-3">
            {profileInfo.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-2xl border border-gray-100 dark:border-gray-600"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                    {item.label}
                  </p>
                  <p className="text-sm text-gray-900 dark:text-gray-100 font-semibold truncate">
                    {item.value || "Not provided"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Current Product & Role */}
        {productDetails && (
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Current Selection
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-4 p-4 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-2xl border border-orange-200 dark:border-orange-800">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Product</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100 font-semibold">
                    {productDetails.productName}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200 dark:border-blue-800">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <UserCog className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Role</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100 font-semibold">
                    {productDetails.roleName}
                  </p>
                  {productDetails.userDivision && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Division: {productDetails.userDivision}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Additional Info */}
        {user?.RA_name && (
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Reporting Manager
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Name</span>
                <span className="text-sm text-gray-900 dark:text-gray-100 font-semibold">
                  {user.RA_name}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Designation</span>
                <span className="text-sm text-gray-900 dark:text-gray-100 font-semibold">
                  {user.RA_designation}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Level</span>
                <span className="text-sm text-gray-900 dark:text-gray-100 font-semibold">
                  {user.RA_level}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Help Requests Link */}
        {isHelpRequestEnabled() && (
          <button
            onClick={() => navigate("/help-requests")}
            className="w-full flex items-center justify-between p-5 bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all active:scale-95 mb-4"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <Headset className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  My Help Requests
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  View and track your support tickets
                </p>
              </div>
            </div>
            <ChevronRight className="w-6 h-6 text-gray-400" />
          </button>
        )}

        {/* Settings Link */}
        <button
          onClick={() => navigate("/settings")}
          className="w-full flex items-center justify-between p-5 bg-white dark:bg-gray-800 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all active:scale-95"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
              <SettingsIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">
                Settings
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Change product, role & preferences
              </p>
            </div>
          </div>
          <ChevronRight className="w-6 h-6 text-gray-400" />
        </button>
      </div>
    </div>
  );
}

export default Profile;
